import { z } from "zod";

export const ProductDefaultCostItem = z.object({
  id: z.number(),
  productId: z.number(),
  catalogItemId: z.number().nullable().optional(),
  name: z.string(),
  quantity: z.number(),
  unit: z.string().nullable().optional(),
  unitCost: z.number(),
  totalCost: z.number(),
  supplier: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  type: z.enum(["labour", "materials", "fees"]).optional(),
});

export type ProductDefaultCostItem = z.infer<typeof ProductDefaultCostItem>;

export const BulkUpdateCatalogTypeBody = z.object({
  ids: z.array(z.number()),
  type: z.enum(["labour", "materials", "fees"]),
});

export type BulkUpdateCatalogTypeBody = z.infer<typeof BulkUpdateCatalogTypeBody>;

export const BulkUpdateCatalogCategoryBody = z.object({
  ids: z.array(z.number()),
  category: z.string(),
});

export type BulkUpdateCatalogCategoryBody = z.infer<typeof BulkUpdateCatalogCategoryBody>;

export const BulkUpdateProductCategoryBody = z.object({
  ids: z.array(z.number()),
  category: z.string(),
});

export type BulkUpdateProductCategoryBody = z.infer<typeof BulkUpdateProductCategoryBody>;

export const MilestoneItem = z.object({
  id: z.number(),
  invoiceId: z.number(),
  name: z.string(),
  percentage: z.number().nullable().optional(),
  amount: z.number().nullable().optional(),
  status: z.enum(["pending", "invoiced", "paid"]),
  notes: z.string().nullable().optional(),
  sortOrder: z.number(),
  childInvoiceId: z.number().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type MilestoneItem = z.infer<typeof MilestoneItem>;

export const MilestoneInput = z.object({
  id: z.number().optional(),
  name: z.string().min(1),
  percentage: z.number().min(0).max(100).nullable().optional(),
  amount: z.number().min(0).nullable().optional(),
  notes: z.string().nullable().optional(),
  sortOrder: z.number().optional(),
}).refine(
  (m) => {
    const hasPct = m.percentage !== null && m.percentage !== undefined;
    const hasAmt = m.amount !== null && m.amount !== undefined;
    return hasPct || hasAmt;
  },
  { message: "Each milestone must have either a percentage or a fixed amount" }
).refine(
  (m) => {
    const hasPct = m.percentage !== null && m.percentage !== undefined;
    const hasAmt = m.amount !== null && m.amount !== undefined;
    return !(hasPct && hasAmt);
  },
  { message: "Each milestone cannot have both a percentage and a fixed amount" }
);

export type MilestoneInput = z.infer<typeof MilestoneInput>;

export const UpsertMilestonesBody = z.object({
  milestones: z.array(MilestoneInput),
});

export type UpsertMilestonesBody = z.infer<typeof UpsertMilestonesBody>;

export const ListMilestonesResponse = z.array(MilestoneItem);
export type ListMilestonesResponse = z.infer<typeof ListMilestonesResponse>;

export const EstimateMilestoneItem = z.object({
  id: z.number(),
  estimateId: z.number(),
  name: z.string(),
  percentage: z.number().nullable().optional(),
  amount: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  sortOrder: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EstimateMilestoneItem = z.infer<typeof EstimateMilestoneItem>;

export const EstimateMilestoneInput = z.object({
  id: z.number().optional(),
  name: z.string().min(1),
  percentage: z.number().min(0).max(100).nullable().optional(),
  amount: z.number().min(0).nullable().optional(),
  notes: z.string().nullable().optional(),
  sortOrder: z.number().optional(),
}).refine(
  (m) => {
    const hasPct = m.percentage !== null && m.percentage !== undefined;
    const hasAmt = m.amount !== null && m.amount !== undefined;
    return hasPct || hasAmt;
  },
  { message: "Each milestone must have either a percentage or a fixed amount" }
).refine(
  (m) => {
    const hasPct = m.percentage !== null && m.percentage !== undefined;
    const hasAmt = m.amount !== null && m.amount !== undefined;
    return !(hasPct && hasAmt);
  },
  { message: "Each milestone cannot have both a percentage and a fixed amount" }
);
export type EstimateMilestoneInput = z.infer<typeof EstimateMilestoneInput>;

export const UpsertEstimateMilestonesBody = z.object({
  milestones: z.array(EstimateMilestoneInput),
});
export type UpsertEstimateMilestonesBody = z.infer<typeof UpsertEstimateMilestonesBody>;

export const ListEstimateMilestonesResponse = z.array(EstimateMilestoneItem);
export type ListEstimateMilestonesResponse = z.infer<typeof ListEstimateMilestonesResponse>;

export const CreateInstallmentsBody = z.object({
  installments: z.array(z.object({
    name: z.string(),
    amount: z.number().min(0),
    dueDate: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
  })),
});
export type CreateInstallmentsBody = z.infer<typeof CreateInstallmentsBody>;

const InstallmentItem = z.object({
  id: z.number(),
  invoiceId: z.number(),
  name: z.string(),
  amount: z.number(),
  dueDate: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  status: z.enum(["pending", "paid"]),
  sortOrder: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CreateInstallmentsResponse = z.object({
  installments: z.array(InstallmentItem),
});
export type CreateInstallmentsResponse = z.infer<typeof CreateInstallmentsResponse>;

export const ListInstallmentsResponse = z.array(InstallmentItem);
export type ListInstallmentsResponse = z.infer<typeof ListInstallmentsResponse>;

export const CompleteMilestoneResponse = z.object({
  milestone: MilestoneItem,
  childInvoiceId: z.number(),
  childInvoice: z.object({
    id: z.number(),
    title: z.string().nullable(),
    status: z.string(),
    total: z.number().nullable(),
    milestoneParentId: z.number().nullable(),
  }),
});
export type CompleteMilestoneResponse = z.infer<typeof CompleteMilestoneResponse>;
